/**
 * A small flag image for a locale / language code. Uses flagcdn.com SVGs at
 * runtime (no npm dependency, matching the app's CDN-for-assets approach, e.g.
 * Leaflet) so flags render reliably everywhere — unlike flag *emoji*, which
 * Windows/Chrome show as plain letter pairs (GB, IQ, …).
 *
 * Kurdish is the exception, and is DRAWN below rather than fetched: flagcdn is
 * keyed on ISO 3166-1 country codes and Kurdistan has none, so there is no URL
 * to ask for. It used to fall back to a ☀️ emoji for `ckb` and a 🌐 globe for
 * everything else — which meant Badini, added later as `kmr`, quietly got the
 * globe. Both varieties now get the real flag.
 */
const LOCALE_CC: Record<string, string> = {
  en: 'gb',
  ar: 'iq', // IQ ERP context — Iraqi Arabic
  fr: 'fr',
  de: 'de',
  es: 'es',
  it: 'it',
  pt: 'pt',
  tr: 'tr',
  fa: 'ir',
  ru: 'ru',
  zh: 'cn',
  hi: 'in',
  ur: 'pk',
  he: 'il',
};

/** Every Kurdish tag this app can be set to. `ku` is the macrolanguage. */
const KURDISH = new Set(['ckb', 'kmr', 'ku', 'kur']);

/**
 * The flag of the Kurdistan Region: red / white / green bands under a golden
 * sun of exactly TWENTY-ONE rays — the count is the point of the emblem, so the
 * rays are generated from that number rather than hand-drawn into a path where
 * a miscount could never be spotted. Colours are the KRG's published ones.
 */
function KurdistanFlag({ className }: { className?: string }): JSX.Element {
  const cx = 450;
  const cy = 300;
  const step = (2 * Math.PI) / 21;
  // Each ray fills 90% of its slot, leaving a hairline of band colour between
  // neighbours. Tuned against the real flag: narrower rays read as a spidery
  // asterisk at any size, wider ones merge into a plain disc.
  const half = step * 0.45;
  const at = (r: number, a: number): string =>
    `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;

  return (
    // The one place in this app that may hard-code colour. `no-restricted-syntax`
    // exists so UI colour comes from brand tokens and re-themes with the palette
    // — and a flag is precisely the thing that must NOT. These four values are
    // the KRG's published ones; a Kurdistan flag whose red follows the customer's
    // brand colour is not a Kurdistan flag.
    /* eslint-disable no-restricted-syntax */
    <svg viewBox="0 0 900 600" className={className} aria-hidden focusable="false">
      <rect x="0" y="0" width="900" height="200" fill="#ED2024" />
      <rect x="0" y="200" width="900" height="200" fill="#FFFFFF" />
      <rect x="0" y="400" width="900" height="200" fill="#278E43" />
      <g fill="#FEBD11">
        {Array.from({ length: 21 }, (_, i) => {
          // −90° so a ray points at twelve o'clock, as the flag is drawn.
          const a = i * step - Math.PI / 2;
          return <polygon key={i} points={`${at(165, a)} ${at(70, a - half)} ${at(70, a + half)}`} />;
        })}
        <circle cx={cx} cy={cy} r={74} />
      </g>
    </svg>
    /* eslint-enable no-restricted-syntax */
  );
}

export function LocaleFlag({
  code,
  className = 'h-4 w-auto rounded-[2px] ring-1 ring-border/50',
}: {
  code: string;
  className?: string;
}): JSX.Element {
  const primary = (code.split('-')[0] ?? '').toLowerCase();
  if (KURDISH.has(primary)) return <KurdistanFlag className={className} />;
  const cc = LOCALE_CC[primary];
  if (cc) {
    return <img src={`https://flagcdn.com/${cc}.svg`} alt="" loading="lazy" className={className} />;
  }
  // A language with no flag of its own — a globe, never a guessed country.
  return (
    <span aria-hidden className="inline-grid place-items-center text-sm leading-none">
      🌐
    </span>
  );
}
